'use client';

import React, { useState } from 'react';
import PropertyGallery from './PropertyGallery';

import PropertyTabs from './PropertyTabs';
import AgentCard from './AgentCard';
import MortgageCalculator from './MortgageCalculator';
import InvestmentAnalysis from './InvestmentAnalysis';
import SimilarProperties from './SimilarProperties';
import PropertyMap from './PropertyMap';
import { Heart, Share2, BarChart2, Bell, CheckCircle, TrendingUp, Eye, Calendar, Box } from 'lucide-react';
import SavePropertyButton from '@/components/SavePropertyButton';

export default function PropertyDetailClient() {
  const [isFavorited, setIsFavorited] = useState(false);
  const [activeTab, setActiveTab] = useState<'genel' | 'yatirim' | 'konum' | 'benzer'>('genel');

  const PROPERTY = {
    id: 'detail-001',
    title: 'Kadıköy Moda\'da Deniz Manzaralı Lüks 3+1 Daire',
    price: '₺12.750.000',
    pricePerM2: '₺85.000',
    priceNum: 12750000,
    address: 'Moda Cad. No:42 D:8, Kadıköy / İstanbul',
    listingType: 'satilik' as const,
    type: 'Daire',
    views: 4821,
    daysListed: 3,
    listingNo: 'EVR-2026-84521',
    badges: ['one-cikan', 'dogrulanmis', '3d', 'video'] as Array<'one-cikan' | 'dogrulanmis' | '3d' | 'video'>,
    investmentScore: 82,
    rentalYield: '5.8',
    estimatedRent: '₺45.000 – ₺52.000',
    amortization: '18.4',
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <a href="/" className="hover:text-primary transition-colors">Ana Sayfa</a>
        <span>/</span>
        <a href="/property-search?il=istanbul" className="hover:text-primary transition-colors">İstanbul</a>
        <span>/</span>
        <a href="/property-search?ilce=kadikoy" className="hover:text-primary transition-colors">Kadıköy</a>
        <span>/</span>
        <span className="text-foreground font-medium">3+1 Daire</span>
      </nav>

      {/* Page Title Row */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="bg-primary text-white text-xs font-bold px-2.5 py-1 rounded-lg">Satılık</span>
            {PROPERTY.badges.includes('dogrulanmis') && (
              <span className="badge-verified flex items-center gap-1"><CheckCircle size={11} />Doğrulanmış İlan</span>
            )}
            {PROPERTY.badges.includes('one-cikan') && (
              <span className="badge-featured">Öne Çıkan</span>
            )}
            {PROPERTY.badges.includes('3d') && (
              <span className="badge-3d flex items-center gap-1"><Box size={10} />3D Tur</span>
            )}
          </div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-foreground leading-tight mb-2">
            {PROPERTY.title}
          </h1>
          <p className="text-muted-foreground text-sm flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-primary flex-shrink-0">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {PROPERTY.address}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setIsFavorited(!isFavorited)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all duration-150 ${isFavorited ? 'bg-danger-light text-danger border-danger/30' : 'border-border text-foreground hover:bg-danger-light hover:text-danger hover:border-danger/30'}`}
          >
            <Heart size={16} fill={isFavorited ? 'currentColor' : 'none'} />
            <span className="hidden sm:inline">{isFavorited ? 'Favorilendi' : 'Favorile'}</span>
          </button>
          <SavePropertyButton
            property={{
              propertyId: PROPERTY.id,
              propertyTitle: PROPERTY.title,
              propertyPrice: PROPERTY.price,
              propertyLocation: PROPERTY.address,
              propertyType: PROPERTY.type,
              listingType: PROPERTY.listingType,
            }}
            variant="button"
          />
          <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-all duration-150">
            <Share2 size={16} />
            <span className="hidden sm:inline">Paylaş</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-all duration-150">
            <BarChart2 size={16} />
            <span className="hidden sm:inline">Karşılaştır</span>
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5"><Eye size={14} className="text-primary" />{PROPERTY.views.toLocaleString('tr-TR')} görüntüleme</span>
        <span className="flex items-center gap-1.5"><Calendar size={14} className="text-primary" />{PROPERTY.daysListed === 0 ? 'Bugün eklendi' : `${PROPERTY.daysListed} gün önce`}</span>
        <span className="flex items-center gap-1.5"><Bell size={14} className="text-primary" />İlan No: {PROPERTY.listingNo}</span>
        <span className="flex items-center gap-1.5 font-semibold text-success"><TrendingUp size={14} />Kira Getirisi: %{PROPERTY.rentalYield}</span>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-8">
        {/* Left: Gallery + Content */}
        <div className="xl:col-span-2">
          {/* Gallery */}
          <PropertyGallery />

          {/* Tabs */}
          <div className="mt-6">
            <div className="flex border-b border-border mb-6 overflow-x-auto scrollbar-hide">
              {[
                { id: 'genel', label: 'Genel Bilgiler' },
                { id: 'yatirim', label: 'Yatırım Analizi' },
                { id: 'konum', label: 'Konum & Çevre' },
                { id: 'benzer', label: 'Benzer İlanlar' },
              ].map((tab) => (
                <button
                  key={`tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`flex-shrink-0 px-5 py-3 text-sm font-semibold border-b-2 transition-all duration-150 ${activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'genel' && <PropertyTabs property={PROPERTY} />}
            {activeTab === 'yatirim' && <InvestmentAnalysis property={PROPERTY} />}
            {activeTab === 'konum' && <PropertyMap />}
            {activeTab === 'benzer' && <SimilarProperties />}
          </div>
        </div>

        {/* Right: Sticky Sidebar */}
        <div className="space-y-5">
          {/* Price Card */}
          <div className="card-elevated rounded-2xl p-6 sticky top-24">
            <div className="mb-5">
              <p className="text-3xl font-extrabold text-foreground tabular-nums">{PROPERTY.price}</p>
              <p className="text-muted-foreground text-sm tabular-nums mt-0.5">{PROPERTY.pricePerM2}/m²</p>

              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border">
                <div className="flex-1 text-center">
                  <p className="text-xs text-muted-foreground">Tahmini Kira</p>
                  <p className="text-sm font-bold text-success tabular-nums">{PROPERTY.estimatedRent}/ay</p>
                </div>
                <div className="w-px h-8 bg-border" />
                <div className="flex-1 text-center">
                  <p className="text-xs text-muted-foreground">Amortisman</p>
                  <p className="text-sm font-bold text-foreground">{PROPERTY.amortization} yıl</p>
                </div>
                <div className="w-px h-8 bg-border" />
                <div className="flex-1 text-center">
                  <p className="text-xs text-muted-foreground">Yatırım Skoru</p>
                  <p className="text-sm font-bold text-primary">{PROPERTY.investmentScore}/100</p>
                </div>
              </div>
            </div>

            <AgentCard />
          </div>

          {/* Mortgage Calculator */}
          <MortgageCalculator propertyPrice={PROPERTY.priceNum} />
        </div>
      </div>
    </div>
  );
}